<template>
  <v-menu 
    v-bind="attrs.componentAttrs"
    @update:model-value="onMenuToggle"
  >
    <template #activator="{ props }">
      <app-button :type="computedType"
                  :size="size"
                  :variant="variant"
                  :full-width="fullWidth"
                  :class="computedButtonClasses"
                  :disabled="disabled"
                  v-bind="props"
                  :data-locator="attrs.dataLocator"
      >
        <template #left-icon>
          <!-- @slot for left icon -->
          <slot name="left-icon"></slot>
        </template>

        <!-- @slot for button text -->
        <slot></slot>

        <template v-if="!iconOnly" #right-icon>
          <app-icon-ds size="xxs" icon="chevron-down" />
        </template>
      </app-button>
    </template>

    <!-- @slot for list -->
    <slot name="list">
      <v-list>
        <app-dropdown-button-item v-for="(item, index) in menuItems"
          :key="`menuItem_${index}`"
          :disabled="item.loading"
          :icon="item.icon"
          :img="item.img"
          :label="item.title"
          :data-locator="item.locatorValue"
          @click="handleItemClick(item)"
        />
      </v-list>
    </slot>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import { useDataLocator } from '@/common/utils/useDataLocator'

import { AppButtonSize, AppButtonType, AppButtonVariant } from '../AppButton.vue'

type MenuItemType = {
  title: string,
  action: Function,
  loading?: boolean,
  icon?: string,
  img?: string,
  locatorValue?: string
}

/**
 * @displayName Dropdown Button
 */
export default defineComponent({
  name: 'AppDropdownButton',
  inheritAttrs: false,
  emits: ['menu-open', 'menu-close'],
  setup () {
    const { attrs } = useDataLocator()
    return { attrs }
  },
  props: {
    disabled: {
      type: Boolean,
    },
    /**
     * Size of the button
     * Possible values are : small, medium
     */
    size: {
      type: String as PropType<AppButtonSize>,
      default: 'medium',
    },
    /**
     * Hierarchy of the button
     * Possible values are : primary, secondary, tertiary
     */
    type: {
      type: String as PropType<AppButtonType>,
      default: 'primary',
    },
    /**
     * Color variant
     * Possible values are : brand, project, dataroom, archive, neutral
     */
    variant: {
      type: String as PropType<AppButtonVariant>,
      default: 'brand',
    },
    /**
     * Dropdown menu options
     */
    menuItems: {
      type: Array as PropType<Array<MenuItemType>>,
      default: () => [],
    },
    /**
     * Specifies whether the button should fill parent's width
     */
    fullWidth: {
      type: Boolean,
      default: false,
    },
    /**
     * Classes added to the button.
     */
    buttonClasses: {
      type: [String, Array],
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedType () {
      return this.iconOnly ? 'icon' : this.type
    },
    computedButtonClasses () {
      let classes: string[] = []
      
      if (Array.isArray(this.buttonClasses)) {
        classes = [...(this.buttonClasses as string[])]
      } else if (this.buttonClasses) {
        classes = [this.buttonClasses as string]
      }
      
      if (this.iconOnly) {
        classes.push('icon-only')
      }
      
      return classes
    },
  },
  methods: {
    handleItemClick (item: MenuItemType) {
      item.action(item)
    },
    onMenuToggle (isOpen: boolean) {
      this.$emit(isOpen ? 'menu-open' : 'menu-close')
    },
  },
})
</script>

<style lang="scss" scoped>
  :deep(.v-divider) {
    margin: 4px 8px;
  }

  .icon-only {
    min-width: 28px !important;
    min-height: 28px !important;
    height: 28px !important;
    
    &:hover {
      background-color: rgba(var(--v-theme-grey-darken-4), 0.05) !important;
      border-radius: 4px;
    }
  }
</style>